"use client";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Progress } from "@/components/ui/progress";
import { htParse } from "@/lib/utils";
import { OptionsContext } from "@/provider/options-provider";
import { useContext, useEffect, useState } from "react";
import { SummarySheet } from "./summary-sheet";
import { SearchItem } from "@/components/search-item";
import { Histogram } from "./histogram";

export function SearchResults() {
  const { searchOptions, searchResults, searchText, params, itemCount } =
    useContext(OptionsContext);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [summary, setSummary] = useState<string | undefined>();
  const [progress, setProgress] = useState(0);

  const max = parseInt(searchOptions.maxSummaryItems) || 0;

  useEffect(() => {
    const searchItemsToSummarize = searchResults?.slice(0, max).map((i) => {
      return { id: i.id, type: i.shortCode };
    });

    const summarizeItems = async (
      groupSummaryItems: {
        id: number;
        type: string;
      }[],
    ) => {
      setSummary(undefined);
      setLoadingSummary(true);
      setProgress(10);
      const response = await fetch(`/api/summarize`, {
        method: "POST",
        body: JSON.stringify({
          ids: groupSummaryItems.map((item) => `${item.type}${item.id}`),
          summarizer_name: searchOptions.summarizer_name,
        }),
        cache: "default",
      });
      setProgress(70);
      if (response.ok) {
        const result = await response.json();
        setSummary(result.summary_html);

        setProgress(90);
      } else {
        setSummary("Failed to summarize");
      }
      setLoadingSummary(false);
      setProgress(100);
    };

    if (searchItemsToSummarize && searchItemsToSummarize.length > 0) {
      summarizeItems(searchItemsToSummarize);
    } else {
      setSummary(undefined);
    }
  }, [max, searchResults, searchOptions.summarizer_name]);

  return (
    <>
      {searchResults && (
        <div className="flex flex-col">
          <div className="flex flex-col justify-between lg:flex-row">
            <div className="flex flex-col space-y-1">
              <h4 className="text-lg font-medium leading-none">
                {params.search_text || searchText}
              </h4>
              {params.ticker && (
                <h4 className="text-lg font-medium leading-none">
                  {params.ticker.toUpperCase()}
                </h4>
              )}
              <p className="text-sm text-muted-foreground">
                Search results from{" "}
                {`${searchOptions.dateRange?.from?.toDateString()} to ${searchOptions.dateRange?.to?.toDateString()}`}{" "}
                (showing {searchResults.length} of {itemCount} items)
              </p>
            </div>
            <div className="flex p-2">
              <Histogram></Histogram>
            </div>
          </div>
          {max > 0 && (
            <div>
              <Accordion type="multiple" defaultValue={["item-1"]}>
                <AccordionItem value="item-1">
                  <AccordionTrigger>
                    Summary of the first {max} items
                  </AccordionTrigger>
                  <AccordionContent className="rounded-md bg-yellow-100 p-10">
                    {loadingSummary ? (
                      <Progress value={progress} className="w-[60%]" />
                    ) : summary ? (
                      htParse(summary)
                    ) : (
                      "No summary"
                    )}
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
          )}
          <SummarySheet />
          <div className="space-y-2">
            {searchResults?.map((item) => (
              <SearchItem key={item.id} item={item} />
            ))}
          </div>
        </div>
      )}
    </>
  );
}
